import React, { useState } from 'react';
import { Lock, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { isNativeApp } from '../../lib/platform';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * FamilyUpsellCard — slim, on-brand teaser that replaces a premium parent
 * feature when the viewer doesn't have family-tier access. Tap it → opens
 * the Family upgrade dialog → routes to Stripe checkout via the existing
 * /api/subscription/checkout-recurring endpoint with plan_id=family_monthly.
 *
 * Public viewers (no auth token) see a "Sign in to unlock" variant that
 * just nudges toward registration.
 *
 * Props:
 *   feature  – short label like "YOUR KID IS UP"
 *   blurb    – one-line value statement
 *   compact  – render inline (no Card padding) if true
 *   icon     – lucide icon component, defaults to Lock
 */
export const FamilyUpsellCard = ({ feature, blurb, compact = false, icon: Icon = Lock, testid = 'family-upsell' }) => {
  const [open, setOpen] = useState(false);
  if (isNativeApp()) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testid}
        className="w-full text-left rounded-xl transition-all hover:scale-[1.01] active:scale-99"
        style={{
          background: 'linear-gradient(135deg, rgba(212, 160, 58, 0.18) 0%, rgba(26, 37, 66, 0.95) 70%)',
          border: '1px solid rgba(245, 196, 85, 0.45)',
          padding: compact ? 10 : 14,
        }}
      >
        <div className="flex items-center gap-3">
          <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(10, 19, 34, 0.55)', border: '1px solid rgba(245, 196, 85, 0.65)' }}>
            <Icon size={16} style={{ color: '#F5C455' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#F5C455' }}>FAMILY · $4.99/mo</p>
            <p className="text-white font-bold text-sm leading-tight">{feature}</p>
            {blurb && <p className="text-slate-300 text-[11px] leading-snug mt-0.5">{blurb}</p>}
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded shrink-0"
            style={{ background: 'linear-gradient(135deg, #F5C455 0%, #D4A03A 100%)', color: '#0A1322' }}>
            Unlock
          </span>
        </div>
      </button>
      <FamilyUpgradeDialog open={open} onOpenChange={setOpen} />
    </>
  );
};


/**
 * FamilyUpgradeDialog — full sales modal for the $4.99 family tier. Public
 * viewers see a "Sign up first" variant that routes to /login. Logged-in
 * users get a Stripe checkout button via the existing recurring endpoint.
 */
export const FamilyUpgradeDialog = ({ open, onOpenChange }) => {
  const [busy, setBusy] = useState(false);
  if (isNativeApp()) return null;
  const token = (() => {
    try {
      const raw = localStorage.getItem('diamond-manager-auth');
      return raw ? (JSON.parse(raw)?.state?.token || null) : null;
    } catch { return null; }
  })();

  const upgrade = async () => {
    if (!token) {
      onOpenChange(false);
      window.location.href = '/login?role=parent';
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/subscription/checkout-recurring`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan_id: 'family_monthly', origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not start checkout');
      if (data.url) window.location.href = data.url;
      else throw new Error('No checkout URL returned');
    } catch (err) {
      toast.error(err.message);
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-0" style={{ background: '#0A1322', border: '1px solid rgba(212, 160, 58, 0.35)' }}>
        <DialogHeader>
          <div className="flex items-center justify-center mb-2">
            <div className="w-14 h-14 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #F5C455 0%, #D4A03A 100%)', boxShadow: '0 0 22px rgba(245,196,85,0.5)' }}>
              <Sparkles size={26} className="text-stone-900" />
            </div>
          </div>
          <DialogTitle className="text-center text-2xl text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Diamond Scorer <span style={{ color: '#F5C455' }}>Family</span>
          </DialogTitle>
          <DialogDescription className="text-center text-slate-300 text-sm">
            $4.99/month · cancel anytime · no surprise fees
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5 my-3">
          {[
            ['⚾', 'YOUR KID IS UP', 'A pulsing emerald banner the second your kid steps to the plate.'],
            ['🔔', 'On-deck push alerts', 'Get notified one batter before — never miss an at-bat.'],
            ['🔊', 'Bat-crack sounds', 'Hear hits and outs in real time when sounds are on.'],
            ['📌', 'Pinned coach notes', 'Field changes, rain delays, snack schedule — pinned to the top.'],
            ['📊', 'Pitch chart + per-player stats', 'Deep dive on your kid\'s at-bats and the day\'s pitching.'],
            ['📹', 'Live video stream', 'Watch the coach\'s broadcast right in the app.'],
            ['📁', 'Game archive', 'Re-watch the play log of any past game, anytime.'],
          ].map(([emoji, title, desc]) => (
            <div key={title} className="flex items-start gap-2.5 p-2 rounded-lg" style={{ background: 'rgba(26, 37, 66, 0.55)' }}>
              <span className="text-lg shrink-0 leading-none">{emoji}</span>
              <div>
                <p className="text-white text-sm font-bold leading-tight">{title}</p>
                <p className="text-slate-400 text-[11px] leading-snug">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <Button
          onClick={upgrade}
          disabled={busy}
          data-testid="family-upgrade-btn"
          className="w-full h-12 text-base font-bold text-stone-900 shadow-lg"
          style={{ background: 'linear-gradient(135deg, #F5C455 0%, #D4A03A 100%)' }}
        >
          {busy ? <Loader2 className="animate-spin mr-2" size={18} /> : <Sparkles size={18} className="mr-2" />}
          {token ? 'Unlock Family · $4.99/mo' : 'Sign up & unlock Family'}
        </Button>
        <p className="text-center text-slate-500 text-[10px]">
          Coaches paying the $7.99 Premium plan already include all Family features for their household.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default FamilyUpsellCard;
